/**
 * Composable for token and domain authentication
 */
import { ref, computed, onMounted } from 'vue'
import { apiService, type DomainValidationResponse } from '../services/api'

export interface AuthState {
  token: string | null
  domain: string | null
  agentId: string | null
  agentName: string | null
  agentNames: string[] | null
  colorTheme: string | null
  isValid: boolean
  isValidating: boolean
  error: string | null
  companyId: string | null
  companyName: string | null
}

export function useAuth() {
  const token = ref<string | null>(null)
  const domain = ref<string | null>(null)
  const agentId = ref<string | null>(null)
  const agentName = ref<string | null>(null)
  const agentNames = ref<string[] | null>(null)
  const colorTheme = ref<string | null>(null)
  const isValid = ref<boolean>(false)
  const isValidating = ref<boolean>(true)
  const error = ref<string | null>(null)
  const companyId = ref<string | null>(null)
  const companyName = ref<string | null>(null)
  
  // Function to select random name from array
  const selectRandomName = (names: string[]): string => {
    if (names.length === 0) return 'AI Assistent'
    if (names.length === 1) return names[0]
    return names[Math.floor(Math.random() * names.length)]
  }

  /**
   * Extract token from URL query parameter
   */
  const extractTokenFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('token') || null
  }

  /**
   * Extract agentId from URL query parameter
   */
  const extractAgentIdFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('agentId') || urlParams.get('agent') || null
  }

  /**
   * Extract domain from current page origin or URL parameter
   */
  const extractDomain = (): string => {
    // First, try to get domain from URL parameter (passed from parent page)
    const urlParams = new URLSearchParams(window.location.search)
    const domainParam = urlParams.get('domain')
    if (domainParam) {
      return domainParam.toLowerCase().replace(/^www\./, '')
    }
    
    // Fallback to hostname (for backwards compatibility)
    const hostname = window.location.hostname
    // Remove www. prefix if present
    return hostname.replace(/^www\./, '')
  }

  /**
   * Validate domain and token
   */
  const validateDomain = async (): Promise<void> => {
    isValidating.value = true
    error.value = null

    // Extract token from URL
    const urlToken = extractTokenFromUrl()
    if (!urlToken) {
      error.value = 'Token is required. Please provide a token in the URL (e.g., ?token=your-token)'
      isValid.value = false
      isValidating.value = false
      return
    }

    token.value = urlToken

    // Extract agentId from URL (optional but recommended)
    const urlAgentId = extractAgentIdFromUrl()
    agentId.value = urlAgentId

    // Extract URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const urlAgentName = urlParams.get('agentName')
    const urlAgentNames = urlParams.get('agentNames')
    
    // Parse agentNames if provided (for reference only)
    if (urlAgentNames) {
      try {
        const parsedNames = JSON.parse(decodeURIComponent(urlAgentNames))
        if (Array.isArray(parsedNames)) {
          agentNames.value = parsedNames
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // SIMPLE LOGIC: Always use agentName from URL if provided
    // The widget is responsible for selecting the random name and passing it
    if (urlAgentName) {
      agentName.value = urlAgentName
    } else {
      agentName.value = 'AI Assistent'
    }
    
    const urlColorTheme = urlParams.get('colorTheme')
    colorTheme.value = urlColorTheme || 'purple'

    // Extract domain from current page
    const currentDomain = extractDomain()
    domain.value = currentDomain

    // Validate domain with backend
    try {
      const validation: DomainValidationResponse = await apiService.validateDomain(
        urlToken,
        currentDomain
      )

      if (validation.isValid) {
        isValid.value = true
        companyId.value = validation.companyId || null
        companyName.value = validation.companyName || null
        error.value = null
      } else {
        isValid.value = false
        error.value = validation.message || 'Domain is not approved for this company'
        companyId.value = null
        companyName.value = null
      }
    } catch (err) {
      isValid.value = false
      error.value = err instanceof Error ? err.message : 'Failed to validate domain'
    } finally {
      isValidating.value = false
    }
  }

  /**
   * Initialize auth on mount
   */
  onMounted(() => {
    validateDomain()
  })

  return {
    token: computed(() => token.value),
    domain: computed(() => domain.value),
    agentId: computed(() => agentId.value),
    agentName: computed(() => agentName.value),
    agentNames: computed(() => agentNames.value),
    colorTheme: computed(() => colorTheme.value),
    isValid: computed(() => isValid.value),
    isValidating: computed(() => isValidating.value),
    error: computed(() => error.value),
    companyId: computed(() => companyId.value),
    companyName: computed(() => companyName.value),
    validateDomain,
    extractTokenFromUrl,
    extractAgentIdFromUrl,
    extractDomain,
    selectRandomName,
  }
}

